import React, { useEffect, useMemo, useState } from "react";
import { useParams, useOutletContext } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { motion, useSpring, useTransform, AnimatePresence } from "framer-motion";
import "./Emissions.css";
import { useLotAnalytics, useLotHourlyData } from "../../hooks/useLotData";
import { useMultiLotAnalytics, useMultiLotHourlyData } from "../../hooks/useMultiLotData";
import { useMultiLot } from "../../contexts/MultiLotContext";

const BASE_EV_COLOR = "#2DCA5F";

const METRIC_CONFIG = [
    { key: "totalElectricVehicles", label: "Electric Vehicles" },
    { key: "totalVehicles", label: "Total Vehicles" },
    { key: "totalEmissions", label: "Total Emissions" }
];

function getOpacity(index: number, total: number) {
    if (total === 1) return 1;
    if (total === 2) return index === 0 ? 1 : 0.5;
    if (total === 3) return [1, 0.66, 0.33][index] || 0.2;
    return 1 - (index / (total * 1.2));
}

function hexToRgba(hex: string, opacity: number) {
    const h = hex.replace('#', '');
    const bigint = parseInt(h, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r},${g},${b},${opacity})`;
}

type DashboardContext = {
    timeframe: string;
    currentRange: { startDate: string; endDate: string };
    previousRange: { startDate: string; endDate: string };
};

type LotMetric = {
    lotId: string;
    timestamp?: string;
    totalVehicles: number;
    totalElectricVehicles: number;
    electricVehicleMakeCount?: { [brand: string]: number };
};

const CustomPieTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        return (
            <div className="emissions-tooltip-box">
                <div className="emissions-tooltip-line1">{data.name}</div>
                <div className="emissions-tooltip-line2">{data.value}</div>
            </div>
        );
    }
    return null;
};

const CustomBarTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        return (
            <div className="emissions-tooltip-box">
                <div className="emissions-tooltip-line1">{data.label}</div>
                <div className="emissions-tooltip-line2">{data.value}</div>
            </div>
        );
    }
    return null;
};

// Helper to compute effective date range for the selected timeframe
function computeEffectiveRange(timeframe: string, range: { startDate: string; endDate: string }) {
    if (!range || !range.startDate || !range.endDate) return range;
    let effective = { ...range };
    const start = new Date(range.startDate);
    const end = new Date(range.endDate);
    const diffDays = (end.getTime() - start.getTime()) / (1000 * 3600 * 24) + 1;
    if (timeframe === 'week' && diffDays > 7) {
        const today = new Date();
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        effective = { startDate: weekStart.toISOString().split('T')[0], endDate: weekEnd.toISOString().split('T')[0] };
    } else if (timeframe === 'month' && diffDays > 31) {
        const today = new Date();
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        effective = { startDate: monthStart.toISOString().split('T')[0], endDate: monthEnd.toISOString().split('T')[0] };
    } else if (timeframe === 'day' && diffDays > 1) {
        const today = new Date();
        const dayStr = today.toISOString().split('T')[0];
        effective = { startDate: dayStr, endDate: dayStr };
    }
    return effective;
}

const Emissions = () => {
    const { lotId } = useParams<{ lotId: string }>();
    const { timeframe, currentRange, previousRange } = useOutletContext<any>();
    const [activeIndex, setActiveIndex] = useState<number>(-1);
    const [barHover, setBarHover] = useState<string | null>(null);

    // Multi-lot context
    const { isMultiLotMode, selectedLots, getSelectedLotIds } = useMultiLot();
    const lotIds = isMultiLotMode ? getSelectedLotIds() : [lotId || ""];

    // Single-lot hooks
    const singleLotCurrentMetrics = useLotAnalytics(
        !isMultiLotMode ? (lotId || "") : "",
        currentRange.startDate,
        currentRange.endDate,
        timeframe
    );

    const singleLotPreviousMetrics = useLotAnalytics(
        !isMultiLotMode ? (lotId || "") : "",
        previousRange.startDate,
        previousRange.endDate,
        timeframe
    );

    const singleLotHourlyData = useLotHourlyData(
        !isMultiLotMode ? (lotId || "") : "",
        currentRange.startDate,
        timeframe
    );

    // Multi-lot hooks
    const multiLotCurrentMetrics = useMultiLotAnalytics(
        isMultiLotMode ? lotIds : [],
        currentRange.startDate,
        currentRange.endDate,
        timeframe,
        selectedLots
    );

    const multiLotPreviousMetrics = useMultiLotAnalytics(
        isMultiLotMode ? lotIds : [],
        previousRange.startDate,
        previousRange.endDate,
        timeframe,
        selectedLots
    );

    const multiLotHourlyData = useMultiLotHourlyData(
        isMultiLotMode ? lotIds : [],
        currentRange.startDate,
        timeframe
    );

    // Select appropriate data based on mode
    const currentMetricsData = isMultiLotMode ? multiLotCurrentMetrics.data : singleLotCurrentMetrics.data;
    const isCurrentLoading = isMultiLotMode ? multiLotCurrentMetrics.isLoading : singleLotCurrentMetrics.isLoading;
    const isCurrentFetching = isMultiLotMode ? multiLotCurrentMetrics.isLoading : singleLotCurrentMetrics.isFetching;

    const previousMetricsData = isMultiLotMode ? multiLotPreviousMetrics.data : singleLotPreviousMetrics.data;
    const isPreviousLoading = isMultiLotMode ? multiLotPreviousMetrics.isLoading : singleLotPreviousMetrics.isLoading;
    const isPreviousFetching = isMultiLotMode ? multiLotPreviousMetrics.isLoading : singleLotPreviousMetrics.isFetching;

    const hourlyDataData = isMultiLotMode ? multiLotHourlyData.data : singleLotHourlyData.data;
    const isHourlyLoading = isMultiLotMode ? multiLotHourlyData.isLoading : singleLotHourlyData.isLoading;
    const isHourlyFetching = isMultiLotMode ? multiLotHourlyData.isLoading : singleLotHourlyData.isFetching;

    // Use hourly data if available and it's today
    const effectiveCurrentData = useMemo(() => {
        if (timeframe === 'day' && hourlyDataData?.data && hourlyDataData.data.length > 0) {
            return hourlyDataData.data;
        }
        return currentMetricsData;
    }, [timeframe, hourlyDataData, currentMetricsData]);

    // Aggregate metrics
    const metrics = useMemo(() => {
        if (!effectiveCurrentData || effectiveCurrentData.length === 0) return {
            totalEV: 0,
            totalVehicles: 0,
            totalEmissions: 0,
            evBrands: {}
        };

        let totalEV = 0;
        let totalVehicles = 0;
        let totalEmissions = 0;
        let evBrands: { [brand: string]: number } = {};

        if (timeframe === 'day' && hourlyDataData?.data && hourlyDataData.data.length > 0) {
            // For hourly data, sum up all values
            hourlyDataData.data.forEach((entry: any) => {
                totalEV += Number(entry.totalElectricVehicles || 0);
                totalVehicles += Number(entry.totalVehicles || 0);
                totalEmissions += Number(entry.totalEmissions || 0);
                if (entry.electricVehicleMakeCount) {
                    Object.entries(entry.electricVehicleMakeCount).forEach(([brand, count]) => {
                        evBrands[brand] = (evBrands[brand] || 0) + Number(count);
                    });
                }
            });
        } else {
            // For other timeframes, use daily data
            effectiveCurrentData.forEach((entry: any) => {
                totalEV += Number(entry.totalElectricVehicles || 0);
                totalVehicles += Number(entry.totalVehicles || 0);
                totalEmissions += Number(entry.totalEmissions || 0);
                if (entry.electricVehicleMakeCount) {
                    Object.entries(entry.electricVehicleMakeCount).forEach(([brand, count]) => {
                        evBrands[brand] = (evBrands[brand] || 0) + Number(count);
                    });
                }
            });
        }

        return {
            totalEV,
            totalVehicles,
            totalEmissions,
            evBrands
        };
    }, [effectiveCurrentData, timeframe, hourlyDataData]);

    const prevMetrics = useMemo(() => {
        if (!previousMetricsData || previousMetricsData.length === 0) return {
            totalEV: 0,
            totalVehicles: 0,
            totalEmissions: 0
        };

        let totalEV = 0;
        let totalVehicles = 0;
        let totalEmissions = 0;

        previousMetricsData.forEach((entry: any) => {
            totalEV += Number(entry.totalElectricVehicles || 0);
            totalVehicles += Number(entry.totalVehicles || 0);
            totalEmissions += Number(entry.totalEmissions || 0);
        });

        return {
            totalEV,
            totalVehicles,
            totalEmissions
        };
    }, [previousMetricsData]);

    // Calculate percentages and changes
    const evPercent = metrics && metrics.totalVehicles > 0 ? (metrics.totalEV / metrics.totalVehicles) * 100 : 0;
    const otherPercent = metrics && metrics.totalVehicles > 0 ? 100 - evPercent : 0;
    const prevEvPercent = prevMetrics && prevMetrics.totalVehicles > 0 ? (prevMetrics.totalEV / prevMetrics.totalVehicles) * 100 : 0;
    const evChange = prevEvPercent !== 0 ? evPercent - prevEvPercent : 0;
    const otherChange = prevEvPercent !== 0 ? (otherPercent - (100 - prevEvPercent)) : 0;

    // Pie chart data for EV brands, sorted by value descending
    const evBrandData = useMemo(() => {
        if (!metrics) return [];
        return Object.entries(metrics.evBrands || {})
            .map(([brand, value]) => ({ name: brand, value }))
            .sort((a, b) => b.value - a.value);
    }, [metrics]);

    // Air quality: 1 EV = 4.6kg CO2 saved
    const co2Saved = metrics ? metrics.totalEV * 4.6 : 0;

    // Bar chart logic
    const barMax = Math.max(metrics?.totalEV || 0, (metrics?.totalVehicles || 0) - (metrics?.totalEV || 0), 1);
    const evBarHeight = metrics && barMax > 0 ? (metrics.totalEV / barMax) * 100 : 0;
    const otherBarHeight = metrics && barMax > 0 ? (((metrics.totalVehicles - metrics.totalEV) / barMax) * 100) : 0;

    // Ottawa EV average horizontal line (fixed at 4% of the bar area height)
    const ottawaEvAvg = 4;
    const ottawaLineHeight = ottawaEvAvg;

    // Pie chart colors (first is #2DCA5F, others are same color with decreasing opacity)
    const pieColors = evBrandData.map((_, idx) => hexToRgba(BASE_EV_COLOR, getOpacity(idx, evBrandData.length)));

    // Bar variants for animations
    const barVariants = {
        hidden: { height: 0 },
        visible: (height: number) => ({
            height: `${height}%`,
            transition: {
                duration: 0.8,
                ease: "easeOut"
            }
        })
    };

    // Animated values using framer-motion springs
    const animatedEV = useSpring(metrics ? metrics.totalEV : 0, { stiffness: 10000, damping: 600 });
    const animatedVehicles = useSpring(metrics ? metrics.totalVehicles : 0, { stiffness: 10000, damping: 600 });
    const animatedEmissions = useSpring(metrics ? metrics.totalEmissions : 0, { stiffness: 10000, damping: 600 });
    const animatedEvPercent = useSpring(evPercent, { stiffness: 10000, damping: 600 });
    const animatedOtherPercent = useSpring(otherPercent, { stiffness: 10000, damping: 600 });

    useEffect(() => {
        if (metrics) {
            animatedEV.set(metrics.totalEV);
            animatedVehicles.set(metrics.totalVehicles);
            animatedEmissions.set(metrics.totalEmissions);
        }
    }, [metrics]);

    useEffect(() => {
        animatedEvPercent.set(evPercent);
        animatedOtherPercent.set(otherPercent);
    }, [evPercent, otherPercent]);

    const displayEV = useTransform(animatedEV, (v: number) => Math.round(v));
    const displayVehicles = useTransform(animatedVehicles, (v: number) => Math.round(v));
    const displayEmissions = useTransform(animatedEmissions, (v: number) => v.toFixed(1));
    const displayEvPercent = useTransform(animatedEvPercent, (v: number) => v.toFixed(1));
    const displayOtherPercent = useTransform(animatedOtherPercent, (v: number) => v.toFixed(1));

    // Helper function to get the correct change text based on timeframe
    const getChangeText = (timeframe: string) => {
        switch (timeframe) {
            case 'day':
                return 'from previous day';
            case 'week':
                return 'from previous week';
            case 'month':
                return 'from previous month';
            case 'year':
                return 'from previous year';
            default:
                return 'from previous period';
        }
    };

    const hasEmissionsData =
        (effectiveCurrentData && effectiveCurrentData.length > 0) ||
        (timeframe === "day" && hourlyDataData?.data && hourlyDataData.data.length > 0);

    const isLoading =
        isCurrentLoading ||
        isPreviousLoading ||
        isHourlyLoading ||
        isCurrentFetching ||
        isPreviousFetching ||
        isHourlyFetching ||
        !metrics ||
        !hasEmissionsData;

    if (isLoading) {
        return (
            <div className="emissions-content emissions-skeleton">
                <div className="emissions-skeleton-top">
                    <div className="skeleton-block emissions-skeleton-top-block" aria-hidden="true" />
                </div>
                <div className="emissions-skeleton-bottom">
                    <div className="skeleton-block emissions-skeleton-bottom-primary" aria-hidden="true" />
                    <div className="skeleton-block emissions-skeleton-bottom-secondary" aria-hidden="true" />
                </div>
            </div>
        );
    }

    return (
        <div className="emissions-content">
            {/* Main metrics box */}
            <div className="emissions-main-box">
                <div className="emissions-metric-section">
                    <div className="emissions-metric-label">Electric Vehicles</div>
                    <div className="emissions-metric-value ev"><motion.span>{displayEvPercent}</motion.span>%</div>
                    <div className="emissions-metric-total">Total: <motion.span>{displayEV}</motion.span> electric vehicles</div>
                    <motion.div className={`emissions-metric-change ${evChange >= 0 ? "up" : "down"}`}
                        key={`ev-change-${evChange}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        {evChange >= 0 ? "+" : ""}{evChange.toFixed(1)}% {getChangeText(timeframe)}
                    </motion.div>
                </div>
                <div className="emissions-metric-section">
                    <div className="emissions-metric-label">Other Vehicles</div>
                    <div className="emissions-metric-value other"><motion.span>{displayOtherPercent}</motion.span>%</div>
                    <div className="emissions-metric-total">Total: <motion.span>{displayVehicles}</motion.span> Other vehicles</div>
                    <motion.div className={`emissions-metric-change ${otherChange < 0 ? "down" : "up"}`}
                        key={`other-change-${otherChange}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        {otherChange >= 0 ? "+" : ""}{otherChange.toFixed(1)}% {getChangeText(timeframe)}
                    </motion.div>
                </div>
                <div className="emissions-bar-section">
                    <div className="emissions-bar-label"></div>
                    <div className="emissions-bar-graph">
                        <div className="emissions-bar-bar">
                            <motion.div
                                className="emissions-bar-ev"
                                custom={evBarHeight}
                                variants={barVariants}
                                initial="hidden"
                                animate="visible"
                                style={{ zIndex: 3 }}
                                onMouseEnter={() => setBarHover("ev")}
                                onMouseLeave={() => setBarHover(null)}
                            />
                            <motion.div
                                className="emissions-bar-other"
                                custom={otherBarHeight}
                                variants={barVariants}
                                initial="hidden"
                                animate="visible"
                                style={{ zIndex: 3 }}
                                onMouseEnter={() => setBarHover("other")}
                                onMouseLeave={() => setBarHover(null)}
                            />
                            {/* Horizontal dotted line at 4% */}
                            <div
                                className="emissions-bar-horizontal-line"
                                style={{ bottom: `${ottawaLineHeight}%`, left: 0, right: 0, position: "absolute", height: 0, zIndex: 4 }}
                            >
                                <div className="emissions-bar-horizontal-line-inner" />
                                <div className="emissions-bar-horizontal-label">Ottawa EV Average: 4%</div>
                            </div>
                            {/* Bar tooltips */}
                            {barHover === "ev" && (
                                <div
                                    className="emissions-tooltip-box"
                                    style={{ left: 150, bottom: `calc(${otherBarHeight}% - 60px)`, zIndex: 10 }}
                                >
                                    <div className="emissions-tooltip-line1">EV's</div>
                                    <div className="emissions-tooltip-line2">{metrics.totalEV.toFixed(2)}</div>
                                </div>
                            )}
                            {barHover === "other" && (
                                <div
                                    className="emissions-tooltip-box"
                                    style={{ left: 150, bottom: `calc(${otherBarHeight}% - 60px)`, zIndex: 10 }}
                                >
                                    <div className="emissions-tooltip-line1">Other</div>
                                    <div className="emissions-tooltip-line2">{(metrics.totalVehicles - metrics.totalEV).toFixed(2)}</div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {/* EV Brands and Air Quality row */}
            <div className="emissions-evbrands-airquality-row">
                <div className="emissions-evbrands-box">
                    <div className="emissions-evbrands-pie">
                        <ResponsiveContainer width="100%" height={220}>
                            <PieChart>
                                <Tooltip content={<CustomPieTooltip />} />
                                <Pie
                                    data={evBrandData as any[]}
                                    cx="40%"
                                    cy="50%"
                                    innerRadius={70}
                                    outerRadius={110}
                                    paddingAngle={1}
                                    dataKey="value"
                                    nameKey="name"
                                    activeIndex={activeIndex}
                                    onMouseEnter={(_, idx) => setActiveIndex(idx)}
                                    onMouseLeave={() => setActiveIndex(-1)}
                                    stroke="none"
                                >
                                    {evBrandData.map((entry, idx) => (
                                        <Cell
                                            key={`cell-${idx}`}
                                            fill={pieColors[idx]}
                                            className={idx === 0 ? "evbrands-dot" : "evbrands-dot secondary"}
                                            style={{ "--evbrand-dot-opacity": getOpacity(idx, evBrandData.length) } as React.CSSProperties}
                                        />
                                    ))}
                                </Pie>
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="emissions-evbrands-legend">
                        <div className="evbrands-title">EV Brands <span className="evbrands-beta">BETA</span></div>
                        <ul className="evbrands-list">
                            {evBrandData.map((entry, idx) => (
                                <li key={entry.name}>
                                    <span
                                        className={idx === 0 ? "evbrands-dot" : "evbrands-dot secondary"}
                                        style={{ background: pieColors[idx], "--evbrand-dot-opacity": getOpacity(idx, evBrandData.length) } as React.CSSProperties}
                                    ></span>
                                    <span style={{ fontWeight: 600 }}>{Math.round(entry.value)}</span>&nbsp;<span style={{ fontWeight: 400 }}>{entry.name}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
                <div className="emissions-airquality-box">
                    <div className="airquality-title">Air Quality <span className="airquality-beta">BETA</span></div>
                    <div className="airquality-metric"><motion.span>{co2Saved.toFixed(1)}</motion.span> <span className="airquality-unit">kg CO₂</span></div>
                    <div className="airquality-metric"><span className="airquality-subtitle">Saved</span></div>
                    <div className="airquality-desc">{Math.round(metrics.totalEV)} Electric Vehicles saved {co2Saved.toFixed(1)}kg of CO₂ last week.</div>
                </div>
            </div>
        </div>
    );
};

export default Emissions;
